"use client";

import { useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type TeamMember = { name: string; role: string; image?: string };

// For members without their own photo, each card frames one person out of the shared
// group photo. Keyed by name (not index) so reordering the team never mis-frames a
// face. `origin` is the transform-origin the zoom pulls toward, `zoom` how tight.
const FOCUS: Record<string, { origin: string; zoom: number }> = {
  Daan: { origin: "24% 52%", zoom: 2.05 },
  Michael: { origin: "50% 30%", zoom: 2.05 },
  Dirck: { origin: "66% 52%", zoom: 2.05 },
  Toprak: { origin: "88% 33%", zoom: 2.05 },
};
const DEFAULT_FOCUS = { origin: "50% 40%", zoom: 2.05 };

// Per-person framing for members with their own portrait. `pos` sets which part of
// the photo sits in the card, `zoom` how tight the crop is.
const PORTRAIT: Record<string, { pos: string; zoom: number }> = {
  Daan: { pos: "50% 20%", zoom: 1.32 },
  Michael: { pos: "50% 0%", zoom: 1 },
  Dirck: { pos: "50% 36%", zoom: 1.6 },
};
const PORTRAIT_DEFAULT = { pos: "50% 12%", zoom: 1 };

export function TeamCarousel({
  members,
  image,
  imageAlt,
}: {
  members: TeamMember[];
  image: string;
  imageAlt: string;
}) {
  const trackRef = useRef<HTMLUListElement>(null);

  function scrollByCards(dir: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("li");
    const amount = card ? card.clientWidth + 20 : 280;
    track.scrollBy({ left: dir * amount, behavior: "smooth" });
  }

  return (
    <div className="relative">
      <ul
        ref={trackRef}
        className="no-scrollbar mx-auto flex w-fit max-w-full snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2"
      >
        {members.map((m) => {
          // framing maps are keyed by first name, so full names still match
          const firstName = m.name.split(" ")[0];
          const focus = FOCUS[firstName] ?? DEFAULT_FOCUS;
          const hasOwn = Boolean(m.image);
          const portrait = PORTRAIT[firstName] ?? PORTRAIT_DEFAULT;
          return (
            <li
              key={m.name}
              className="w-[230px] shrink-0 snap-start sm:w-[260px]"
            >
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-mist ring-1 ring-navy/5">
                <Image
                  src={hasOwn ? (m.image as string) : image}
                  alt={hasOwn ? m.name : `${m.name} - ${imageAlt}`}
                  fill
                  // Cards zoom into the source (scale up to 2.05x), so request an
                  // asset sized for the zoomed width, not the 260px card width, or
                  // faces render soft. Capped by the source resolution.
                  sizes="540px"
                  style={
                    hasOwn
                      ? {
                          objectPosition: portrait.pos,
                          transform: `scale(${portrait.zoom})`,
                          transformOrigin: portrait.pos,
                        }
                      : { transform: `scale(${focus.zoom})`, transformOrigin: focus.origin }
                  }
                  className="object-cover"
                />
              </div>
              <div className="mt-3.5 px-1">
                <p className="text-[16px] font-bold text-navy">{m.name}</p>
                <p className="text-[13px] text-muted">{m.role}</p>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Controls */}
      <div className="mt-6 flex justify-center gap-2">
        <button
          type="button"
          onClick={() => scrollByCards(-1)}
          aria-label="Vorige"
          className="grid size-11 place-items-center rounded-full bg-white text-navy shadow-soft ring-1 ring-line transition hover:bg-mist"
        >
          <ChevronLeft className="size-5" />
        </button>
        <button
          type="button"
          onClick={() => scrollByCards(1)}
          aria-label="Volgende"
          className="grid size-11 place-items-center rounded-full bg-white text-navy shadow-soft ring-1 ring-line transition hover:bg-mist"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>
    </div>
  );
}
